import { average } from "@/lib/stats";
import { findPerShareSafeStartYear } from "@/lib/data/split-adjustment";
import type { YearValue } from "@/lib/data/types";

/**
 * Verbindet eine Jahres-Summenreihe (z.B. Umsatz, freier Cashflow) mit der Aktienanzahl
 * desselben Jahres zu einer Je-Aktie-Reihe - fuer Bewertungskennzahlen wie KUV/KCV, die
 * auf demselben Prinzip wie KGV beruhen (backlog.md -> D1/D2, §B.1-Erweiterung).
 * `sharesOutstanding` kommt seit 2026-07-29 bereits split-BEREINIGT aus
 * `getHistorical()` (echte Yahoo-Split-Ereignisse statt Rohwerten, siehe
 * lib/data/split-adjustment.ts) - der `findPerShareSafeStartYear()`-Aufruf hier ist damit
 * im Regelfall ein no-op zweites Sicherheitsnetz, greift aber weiterhin, falls diese
 * Funktion je mit einer NICHT vorbereinigten Aktienanzahl-Reihe aufgerufen wird (z.B. aus
 * einem zukuenftigen zusaetzlichen Aufrufer). Jahre ohne beide Werte (oder vor dem
 * sicheren Startjahr) fallen aus, statt eine verzerrte Quote zu zeigen. Reine Funktion,
 * kein I/O.
 */
export function perShareSeries(total: YearValue[], sharesOutstanding: YearValue[]): YearValue[] {
  const sharesByYear = new Map(sharesOutstanding.map((s) => [s.year, s.value]));
  const safeStartYear = findPerShareSafeStartYear(sharesByYear);
  return total
    .filter((t) => {
      if (safeStartYear !== null && t.year < safeStartYear) return false;
      const s = sharesByYear.get(t.year);
      return s !== undefined && s > 0;
    })
    .map((t) => ({ year: t.year, value: t.value / sharesByYear.get(t.year)! }));
}

/**
 * Buchwert je Aktie, NAEHERUNGSWEISE hergeleitet aus Jahresgewinn und Eigenkapitalrendite
 * (Eigenkapital = Jahresgewinn / ROE, da ROE per Definition Jahresgewinn/Eigenkapital ist),
 * anschliessend ueber `perShareSeries` durch die Aktienanzahl geteilt - fuer die
 * tagesaktuelle KBV-Linie in `DailyMultiplesChart` (Erik 2026-07-15: "mit
 * Naeherungs-Hinweis einbauen"), da weder Yahoo noch SEC im Projekt bislang eine direkt
 * gelieferte Buchwert-/Eigenkapital-je-Aktie-Reihe liefern. Jahre mit ROE <= 0 (Verlustjahr
 * oder negatives Eigenkapital) liefern keinen Punkt statt eine irrefuehrende
 * Vorzeichen-Verdrehung (negativ/negativ = positiv) als echten Buchwert auszugeben. Reine
 * Funktion, kein I/O.
 */
export function deriveBookValuePerShare(
  netIncome: YearValue[],
  roe: YearValue[],
  sharesOutstanding: YearValue[],
): YearValue[] {
  const roeByYear = new Map(roe.filter((r) => r.value > 0).map((r) => [r.year, r.value]));
  const equity = netIncome
    .filter((n) => roeByYear.has(n.year))
    .map((n) => ({ year: n.year, value: n.value / roeByYear.get(n.year)! }))
    .filter((e) => e.value > 0);
  return perShareSeries(equity, sharesOutstanding);
}

export interface DatedValue {
  /** ISO-Datum (YYYY-MM-DD). */
  date: string;
  value: number;
}

export interface DailyMultiplePoint {
  date: string;
  multiple: number;
}

/**
 * Tagesgenaue Multiple-Reihe: Kurs(t) / Kennzahl(t) fuer jeden Punkt in `priceSeries`,
 * wobei die Kennzahl an den in `metricSteps` gegebenen Stichtagen gestuft fortgeschrieben
 * wird (backlog.md -> D1/D2) - deshalb der gezackte statt geglaettete Verlauf. Punkte vor
 * dem ersten bekannten Kennzahl-Stichtag und Kennzahl-Werte <= 0 (z.B. Verlustjahr) werden
 * ausgelassen statt ein bedeutungsloses negatives/unendliches Multiple zu erzeugen.
 *
 * DATENREALITAET: `metricSteps` stuft heute i.d.R. jaehrlich (`HistoricalSeries` hat keine
 * Quartalsgranularitaet), nicht quartalsweise wie im Idealbild - die Funktion selbst ist
 * granularitaetsagnostisch (nimmt jede Stichtag-Folge entgegen), liefert also mit feinerer
 * Eingabe automatisch einen feineren Verlauf, ohne Codeaenderung.
 *
 * Reine Funktion, kein I/O.
 */
export function computeDailyMultiple(
  priceSeries: DatedValue[],
  metricSteps: DatedValue[],
): DailyMultiplePoint[] {
  const sortedSteps = [...metricSteps]
    .filter((s) => s.value > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
  if (sortedSteps.length === 0) return [];

  const sortedPrices = [...priceSeries].sort((a, b) => a.date.localeCompare(b.date));
  const points: DailyMultiplePoint[] = [];
  let stepIndex = -1;

  for (const p of sortedPrices) {
    while (stepIndex + 1 < sortedSteps.length && sortedSteps[stepIndex + 1].date <= p.date) {
      stepIndex++;
    }
    if (stepIndex < 0) continue;
    points.push({ date: p.date, multiple: p.value / sortedSteps[stepIndex].value });
  }
  return points;
}

export interface ForwardMultipleStep {
  year: number;
  value: number;
}

export type ForwardMultipleSource = "analyst" | "extrapolated";

export interface ForwardMultiplePoint {
  year: number;
  multiple: number;
  source: ForwardMultipleSource;
}

/**
 * Prognose-Multiple-Pfad: haelt den Kurs konstant, setzt kuenftige Kennzahlwerte ein ->
 * gestufter Forward-Multiple-Verlauf (backlog.md -> D1/D2, §B.2). Echte Analystenjahre aus
 * `realSteps` zuerst (`source: "analyst"`), danach optional `extrapolateYears` weitere Jahre
 * per `longTermGrowthRate` fortgeschrieben (`source: "extrapolated"`) - Konsumenten MUESSEN
 * beide Quellen visuell unterscheiden (backlog.md -> §0.3, "wachstumsbasierte
 * Extrapolation (X %)" statt als Analystenschaetzung ausgeben). Ohne `longTermGrowthRate`
 * oder ohne jeden echten Schaetzjahr-Wert bricht die Reihe ehrlich ab, statt Jahre zu
 * erfinden. Reine Funktion, kein I/O.
 */
export function computeForwardMultiplePath(
  lastPrice: number,
  realSteps: ForwardMultipleStep[],
  longTermGrowthRate: number | undefined,
  extrapolateYears: number,
): ForwardMultiplePoint[] {
  if (lastPrice <= 0) return [];
  const sorted = [...realSteps].filter((s) => s.value > 0).sort((a, b) => a.year - b.year);
  const points: ForwardMultiplePoint[] = sorted.map((s) => ({
    year: s.year,
    multiple: lastPrice / s.value,
    source: "analyst",
  }));

  if (longTermGrowthRate !== undefined && longTermGrowthRate > -1 && sorted.length > 0 && extrapolateYears > 0) {
    let value = sorted[sorted.length - 1].value;
    let year = sorted[sorted.length - 1].year;
    for (let i = 0; i < extrapolateYears; i++) {
      value *= 1 + longTermGrowthRate;
      year += 1;
      if (value <= 0) break;
      points.push({ year, multiple: lastPrice / value, source: "extrapolated" });
    }
  }
  return points;
}

/**
 * Durchschnitts-Multiple (arithmetisches Mittel) ueber die letzten `rangeYears` Jahre ab
 * `asOf` (Default: heute) - fuer die gestrichelte Referenzlinie im Multiples-Chart.
 * `rangeYears` weggelassen = kompletter Zeitraum. `undefined` ohne Datenpunkte im Zeitraum,
 * nie eine erfundene 0. Reine Funktion, kein I/O.
 */
export function computeAverageMultiple(
  series: DailyMultiplePoint[],
  rangeYears?: number,
  asOf: Date = new Date(),
): number | undefined {
  if (rangeYears === undefined) {
    return average(series.map((s) => s.multiple));
  }
  const cutoff = new Date(
    Date.UTC(asOf.getUTCFullYear() - rangeYears, asOf.getUTCMonth(), asOf.getUTCDate()),
  )
    .toISOString()
    .slice(0, 10);
  const relevant = series.filter((s) => s.date >= cutoff);
  return average(relevant.map((s) => s.multiple));
}
